import { Events, emit, on } from '../core/events.js';
import { t } from '../../shared/i18n/index.js';

let info = null, activeDocId = null, resolveDoc = () => null, resolveUser = () => null;
let pendingUser = null;
const pending = new Set();
const rejected = new Map();
export function configureQuota({ getDoc, getUser }) {
  resolveDoc = getDoc;
  if (getUser) resolveUser = getUser;
}
// Only identifiers are persisted here. Document content stays in its existing
// storage. A quota rejection must still protect local-only work after reload.
function pendingKey() { return `orbital-quota-pending:${pendingUser}`; }
function loadPending() {
  const userId = resolveUser()?.user_id || null;
  if (userId === pendingUser) return;
  pendingUser = userId; pending.clear();
  if (userId) {
    try {
      const ids = JSON.parse(localStorage.getItem(pendingKey()) || '[]');
      if (Array.isArray(ids)) for (const id of ids) if (typeof id === 'string') pending.add(id);
    } catch { /* In-memory tracking still protects this session. */ }
  }
}
function savePending() {
  if (!pendingUser) return;
  try {
    if (pending.size) localStorage.setItem(pendingKey(), JSON.stringify([...pending]));
    else localStorage.removeItem(pendingKey());
  } catch { /* Keep in memory. */ }
}
export function hasDocQuotaPending(docId) { loadPending(); return pending.has(docId); }
export function hasPendingQuotaWrites() { loadPending(); return pending.size > 0; }
export function discardQuotaPending() {
  loadPending(); pending.clear(); savePending();
}
export function forgetQuotaPending(docId) {
  loadPending(); pending.delete(docId); savePending();
}
export function setQuotaActiveDoc(id) { activeDocId = id; }
export function getQuotaInfo() { return info; }
export function quotaLevel(used, quota) {
  if (!Number.isFinite(used) || !Number.isFinite(quota) || used < 0 || quota < 0) return 'unknown';
  if (used > quota) return 'over';
  if (used > quota * .95) return 'near';
  if (used > quota * .90) return 'warning';
  return 'normal';
}
export function accountQuotaBlocked() {
  return info?.quotaScope === 'combined' && (info.totalBytes > info.storageQuota || rejected.has('account'));
}
export function isDocQuotaBlocked(docId = activeDocId) {
  if (!docId || resolveDoc(docId)?._storage === 'local') return false;
  const doc = resolveDoc(docId), row = info?.documents.get(docId);
  return !!(rejected.has(docId) || row?.ownerOverQuota ||
    ((!doc?.role || doc.role === 'owner') && accountQuotaBlocked()));
}
export function assertDocQuota(docId = activeDocId) {
  if (isDocQuotaBlocked(docId)) throw Object.assign(new Error(t('vault.quota.editBlocked')), { code: 'quota_exceeded' });
}
export function assertCreationQuota() {
  if (accountQuotaBlocked()) throw Object.assign(new Error(t('vault.quota.createBlocked')), { code: 'quota_exceeded' });
}
export function applyQuotaInfo(next) {
  const before = info;
  info = next;
  for (const [id, limit] of rejected) {
    if (limit.used == null) { limit.used = next.totalBytes; limit.quota = next.storageQuota; }
    else if (next.totalBytes < limit.used || next.storageQuota > limit.quota) rejected.delete(id);
  }
  emit(Events.QUOTA_CHANGED, { info: next, previous: before });
}
export function recordQuotaRejection(docId, scope = 'map') {
  const id = scope === 'vault' || !docId ? 'account' : docId;
  loadPending(); pending.add(id); savePending();
  if (!rejected.has(id)) rejected.set(id, { used: info?.totalBytes, quota: info?.storageQuota });
  emit(Events.QUOTA_REJECTED, { docId, scope });
  emit(Events.QUOTA_CHANGED, { info });
}
export function clearQuotaRejection(docId, scope = 'map') {
  const id = scope === 'vault' ? 'account' : docId;
  forgetQuotaPending(id);
  if (rejected.delete(id)) emit(Events.QUOTA_CHANGED, { info });
}
export function resetQuotaState() { info = null; activeDocId = null; rejected.clear(); pending.clear(); pendingUser = null; }
on(Events.AUTH_LOGOUT, resetQuotaState);
on(Events.AUTH_LOGIN, resetQuotaState);
